import React from 'react';
import { useRouting } from 'hooks/useRouting';
import { AggregateSourceTypes } from 'config/consts';
import { Link } from 'react-router-dom';
import {
  addSignCommaFormat,
  commaFormat,
} from 'components/features/new-report/utils/formatter';
import { AssetBundleType } from '../Home';
import { StopAggregate } from '../PortfolioStuffing/asset/AssetOrganizedPortfolioForeign';
import { goToGrouping } from './../PortfolioRatioStuffing/PortfolioRatioStuffing';
import {
  AmountColor,
  amountToAmountColor,
} from 'components/features/utils/util';
import { WithEmptyString } from './../WithEmptyView';
import { getAggregateSourceName } from 'components/features/utils/converter';
import { GetUserBrokerResultItem } from 'config/api';

type BrokerItem = {
  brokerID: string;
  brokerName: string;
  brokerType: number;
  aggregateSource: number;
  account: string;
  status: string | null;
  totalAssetAmount: number;
  totalProfitOrLoss: number;
  cashAmount: number;
  otherAmount: number;
  available: boolean;
  updTime: string;
  diffTotalProfitOrLoss: number | null;
};

type GroupAssetStuffingRegisteredAccountListProps = {
  brokers: (BrokerItem & {
    refresh: { isOutOfDate: boolean; account: string } | null;
  })[];
  assetBundleType: AssetBundleType;
};

type GroupAssetStuffingRegisteredAccountProps = {
  aggregate: string;
  canBeAggregated: boolean;
  securityName: string;
  amount: string;
  profitOrLoss: {
    amount: string;
    color: AmountColor;
  } | null;
  brokerId: string;
  refresh: { isOutOfDate: boolean; account: string } | null;
};

const GroupAssetStuffingRegisteredAccountListInternal = ({
  brokers,
  assetBundleType,
}: GroupAssetStuffingRegisteredAccountListProps): React.ReactElement => {
  const title = '登録口座';
  const { pushRouting } = useRouting();
  const editGroup = (): void => {
    pushRouting({ path: `grouping` }, true);
  };

  const groupAssetStuffingRegisteredAccountPropss: GroupAssetStuffingRegisteredAccountProps[] = brokers.map(
    item => {
      const getIsShowProfitOrLoss = (aggregateSource: number): boolean =>
        aggregateSource === AggregateSourceTypes.テコテック;
      return {
        aggregate: getAggregateSourceName(item.aggregateSource),
        profitOrLoss: getIsShowProfitOrLoss(item.aggregateSource)
          ? {
              amount: addSignCommaFormat(item.diffTotalProfitOrLoss, {
                numberOfDecimals: 0,
              }),
              color: amountToAmountColor(item.diffTotalProfitOrLoss ?? 0),
            }
          : null,
        securityName: item.brokerName,
        amount: commaFormat(item.totalAssetAmount, { numberOfDecimals: 0 }),
        brokerId: item.account,
        canBeAggregated: item.available,
        refresh: item.refresh,
      };
    }
  );

  const GroupAssetStuffingRegisteredAccount = ({
    aggregate,
    canBeAggregated,
    securityName,
    amount,
    profitOrLoss,
    brokerId,
    refresh,
  }: GroupAssetStuffingRegisteredAccountProps): React.ReactElement => {
    const { pushRouting } = useRouting();

    return (
      <>
        <div
          className="flex items-center border-b border-bluegray-300 pl-4 py-3 cursor-pointer"
          onClick={(): void =>
            pushRouting({ path: `/accountdetail/${brokerId}` }, true)
          }>
          <div className="flex-1">
            {refresh && (
              <StopAggregate
                {...{
                  refresh,
                }}
              />
            )}
            <div className="flex items-center flex-1 leading-4.5 text-xs mb-1">
              <div className="text-bluegray-500">{aggregate}</div>
              <div
                className={`${
                  canBeAggregated ? 'text-teal-500' : 'bluegray-400'
                } ml-auto`}>
                集計対象{canBeAggregated ? '' : '外'}
              </div>
            </div>
            <div className="flex items-center flex-1 text-bluegray-700 leading-6">
              <div className="text-base font-bold">{securityName}</div>
              <div className="text-base font-bold ml-auto mr-1">{amount}</div>
              <div className="text-xs">円</div>
            </div>
            {profitOrLoss && (
              <div className={`flex items-center leading-6`}>
                <div className="text-sm text-bluegray-500">評価損益</div>
                <div className={`flex text-xs ml-auto ${profitOrLoss.color}`}>
                  <div className="mr-1">{profitOrLoss.amount}</div>
                  <div className="">円</div>
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center ml-4 w-2">
            <img
              className="w-full"
              src="/static/images/icon/icon-arrow-right.svg"
              alt=""
            />
          </div>
        </div>
      </>
    );
  };

  return (
    <>
      <div className="p-4 pb-6">
        <div className="bg-white max-w-md mx-auto">
          <div className="border-b border-bluegray-300 px-4 pb-2">
            <div className="flex items-center">
              <div className="text-lg font-bold">{title}</div>
              <div
                className="text-xs text-bluegray-500 ml-auto cursor-pointer"
                onClick={editGroup}>
                グループ編集
              </div>
            </div>
          </div>
          <div className="px-4">
            <WithEmptyString
              View={(): React.ReactElement => (
                <>
                  {groupAssetStuffingRegisteredAccountPropss.map((p, i) => (
                    <GroupAssetStuffingRegisteredAccount {...p} key={i} />
                  ))}
                </>
              )}
              empty={{
                message: '登録されている口座はありません',
                elementCustomizer: ({ EmptyView }): React.ReactElement => {
                  return (
                    <>
                      <EmptyView />
                      <Link
                        to="/securityaccounts"
                        className="link-primary mt-2 justify-end">
                        <div className="icon-right"></div>
                        口座・資産情報を追加する
                      </Link>
                    </>
                  );
                },
              }}
              isEmpty={groupAssetStuffingRegisteredAccountPropss.length === 0}
            />
            <div
              className="flex items-center justify-center text-base text-bluegray-600 font-bold leading-6 border border-bluegray-400 rounded-lg h-10 mt-6 bg-gray-50 cursor-pointer"
              onClick={goToGrouping(pushRouting)(`${assetBundleType.key}`)}>
              グループを編集
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const GroupAssetStuffingRegisteredAccountList = ({
  assetBundleType,
  getOutOfDateAccount,
  brokers: brokers_,
}: {
  assetBundleType: AssetBundleType;
  getOutOfDateAccount: (item: {
    account: string;
  }) => { account: string } | null;
  brokers: GetUserBrokerResultItem[];
}): React.ReactElement => {
  const brokers = brokers_.map(item => {
    const account = getOutOfDateAccount({
      account: item.Account,
    });
    return {
      brokerID: item.BrokerID,
      brokerName: item.BrokerName,
      brokerType: item.BrokerType,
      aggregateSource: item.AggregateSource,
      account: item.Account,
      status: item.Status,
      totalAssetAmount: item.TotalAssetAmount,
      totalProfitOrLoss: item.TotalProfitOrLoss,
      cashAmount: item.CashAmount,
      otherAmount: item.OtherAmount,
      available: item.Available,
      updTime: item.UpdTime,
      diffTotalAssetAmount: item.DiffTotalAssetAmount,
      diffTotalAssetRate: item.DiffTotalAssetRate,
      diffStockAmount: item.DiffStockAmount,
      diffStockRate: item.DiffStockRate,
      diffMarginAmount: item.DiffMarginAmount,
      diffMarginRate: item.DiffMarginRate,
      diffMarginProfitOrLoss: item.DiffMarginProfitOrLoss,
      diffMarginProfitOrLossRate: item.DiffMarginProfitOrLossRate,
      diffRoboAdvisorAmount: item.DiffRoboAdvisorAmount,
      diffRoboAdvisorRate: item.DiffRoboAdvisorRate,
      diffUSStockAmount: item.DiffUSStockAmount,
      diffUSStockRate: item.DiffUSStockRate,
      diffInvestmentTrustAmount: item.DiffInvestmentTrustAmount,
      diffInvestmentTrustRate: item.DiffInvestmentTrustRate,
      diffCashAmount: item.DiffCashAmount,
      diffCashRate: item.DiffCashRate,
      diffOtherAmount: item.DiffOtherAmount,
      diffOtherRate: item.DiffOtherRate,
      diffTotalProfitOrLoss: item.DiffTotalProfitOrLoss,
      isRegisteredBefore: item.IsRegisteredBefore,
      refresh: account ? { isOutOfDate: true, account: account.account } : null,
    };
  });

  return (
    <GroupAssetStuffingRegisteredAccountListInternal
      {...{ brokers, assetBundleType }}
    />
  );
};
